<template>
  <div>
    <SidbearSettingsGroup
      active
      title="Carousel"
    >
      <b-form-group v-if="linked && !subjectHide.includes('unlink')">
        <button
          class="flex p-0 text-[11px] hover:bg-gray-100 items-center text-dark border rounded-[4px] w-[114px] h-[30px] justify-content-center gap-[5px] cursor-pointer font-[500] hover:text-[#3D82EA] text-[#7E7E7E]"
          @click="() => unlinkSid()"
        >
          Unlink Styles
          <i class="fa fa-link text-primary" />
        </button>
      </b-form-group>

      <b-form-group>
        <draggable
          v-model="section.options.content.carousel.slides"
          handle=".move"
        >
          <transition-group name="shufle">
            <carousel-drag-item
              v-for="(slide, index) in section.options.content.carousel.slides"
              :key="`key-${index}`"
              :item="slide"
              :active="activeSlide === slide.uid"
              @delete="() => deleteSlide(index, slide)"
              @open="() => selectSlide(slide.uid, index)"
            >
              <template #active>
                <sid-background :sid="`.--${section.sid}--${slide.uid}--bg`" />

                <sid-upload-image
                  :sid="`.--${section.sid}--${slide.uid}--bg`"
                  rule-name="background-image"
                />

                <b-form-group label="Background Image Size">
                  <sid-select
                    priority
                    :searchable="false"
                    :options="[
                      {
                        label: 'Normal',
                        value: 'auto',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'auto']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Full Center (Parallax)',
                        value: 'fixed',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'cover']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'fixed']]]
                      },
                      {
                        label: 'Fill 100 % Width',
                        value: '100% auto',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Fill 100 % Width & Height',
                        value: '100% 100%',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Repeat',
                        value: 'repeat',
                        property: 'background-repeat',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'auto']], [`.--${section.sid}--${slide.uid}--bg`, ['background-repeat', 'repeat']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Repeat Horizontally',
                        value: 'repeat-x',
                        property: 'background-repeat',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'auto']], [`.--${section.sid}--${slide.uid}--bg`, ['background-repeat', 'repeat-x']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Repeat Vertically',
                        value: 'repeat-y',
                        property: 'background-repeat',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'auto']], [`.--${section.sid}--${slide.uid}--bg`, ['background-repeat', 'repeat-y']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Cover',
                        value: 'cover',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'cover']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      },
                      {
                        label: 'Contain',
                        value: 'contain',
                        customProperties: [[`.--${section.sid}--${slide.uid}--bg`, ['background-size', 'contain']], [`.--${section.sid}--${slide.uid}--bg`, ['background-attachment', 'unset']]]
                      }
                    ]"
                    :sid="`.--${section.sid}--${slide.uid}--bg`"
                    var="--background-size"
                    rule-name="background-size"
                  />
                </b-form-group>

                <b-form-group label="Background Image Position">
                  <sid-select
                    :searchable="false"
                    :options="[
                      {label: 'Left Top', value: 'left top'},
                      {label: 'Left Center', value: 'left center'},
                      {label: 'Left Bottom', value: 'left bottom'},
                      {label: 'Right Top', value: 'right top'},
                      {label: 'Right Center', value: 'right center'},
                      {label: 'Right Bottom', value: 'right bottom'},
                      {label: 'Center Top', value: 'center top'},
                      {label: 'Center Center', value: 'center center'},
                      {label: 'Center Bottom', value: 'center bottom'},
                    ]"
                    :sid="`.--${section.sid}--${slide.uid}--bg`"
                    rule-name="background-position"
                  />
                </b-form-group>

                <b-form-group
                  label="Shadow Color"
                  class="mb-0"
                >
                  <sid-box-shadow
                    :sid="`.--${section.sid}--${slide.uid}--bg`"
                    rule-name="box-shadow"
                  />
                </b-form-group>
              </template>
            </carousel-drag-item>
          </transition-group>
        </draggable>

        <button
          class="flex items-center border gap-[5px] rounded-[4px] font-[600] text-dark !text-[11px] h-[29px] px-[18px] shadow-[0_2px_3px_rgba(0, 0, 0, 0.06)] mb-[25px]"
          @click="addSlide"
        >
          Add Slide
          <span class="text-[#3D82EA] text-[20px]">+</span>
        </button>

        <sid-range-slider
          label="Gap"
          :sid="`.--${section.sid}--slider`"
          rule-name="gap"
        />

        <sid-range-slider
          :sid="`.--${section.sid}--slider`"
          rule-name="width"
          label="Slider Width"
          var="--width"
          :dimensions="['px', 'vw', '%']"
          default-dimension="%"
          measurement="width"
        />

        <sid-range-slider
          :sid="`.--${section.sid}--slider`"
          rule-name="height"
          label="Slider Height"
          :dimensions="['px', 'vh']"
          default-dimension="vh"
          var="--height"
          measurement="height"
        />

        <b-form-group>
          <b-checkbox v-model="section.options.content.carousel.autoplay">
            Autoplay
          </b-checkbox>
        </b-form-group>

        <b-form-group>
          <b-checkbox v-model="section.options.content.carousel.pauseOnHover">
            Pause On Hover
          </b-checkbox>
        </b-form-group>

        <b-form-group label="Speed (MS)">
          <LinkedControlItem
            v-model="section.options.content.carousel.speed"
            :max="10000"
            :min="1000"
            :step="1000"
            class="d-block !w-[60px]"
          />
        </b-form-group>

        <b-form-group class="mb-0">
          <label class="flex items-center justify-between">
            <span>Transfer Control</span>

            <i
              v-b-tooltip.hover
              title="You can transfer control of this Carousel to another on this page. Click the 'Select' button first and then click on another Carousel to connect."
              class="fa fa-question-circle cursor-pointer hover:text-blue-500 ml-1"
            />
          </label>

          <pick-element
            v-model="section"
            :allow-picks="['ElCarouselV2']"
          />
        </b-form-group>
      </b-form-group>

      <b-form-group
        label="Animation Speed"
        class="mb-0"
      >
        <sid-select
          :sid="`.--${section.sid}--slider > .slide-container > .slide`"
          rule-name="transition"
          :searchable="false"
          :options="[{label: 'Slow', value: 'transform 1s ease 0s, opacity 1s ease 0s'}, {label: 'Normal', value: 'transform 0.6s ease 0s, opacity 0.6s ease 0s'}, {label: 'Fast', value: 'transform 0.4s ease 0s, opacity 0.4s ease 0s'}]"
        />
      </b-form-group>
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      title="Slides"
    >
      <rule-tabs
        :stack="true"
        :tabs="['Normal', 'Prev', 'Next', 'Active', 'Behind', 'Forward']"
      >
        <template #Normal>
          <sid-transform
            :sid="`.--${section.sid}--slider > .slide-container > .slide`"
            rule-name="transform"
            :section="section"
          />

          <sid-range-slider
            label="Opacity"
            rule-name="opacity"
            :dimensions="[]"
            default-dimension=""
            :sid="`.--${section.sid}--slider > .slide-container > .slide`"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </template>

        <template #Prev>
          <sid-transform
            :sid="`.--${section.sid}--slider > .slide-container.prev > .slide`"
            rule-name="transform"
            :section="section"
            var="--prev-transform"
          />

          <sid-range-slider
            label="Opacity"
            rule-name="opacity"
            :dimensions="[]"
            default-dimension=""
            :sid="`.--${section.sid}--slider > .slide-container.prev > .slide`"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </template>

        <template #Next>
          <sid-transform
            :sid="`.--${section.sid}--slider > .slide-container.next > .slide`"
            rule-name="transform"
            :section="section"
            var="--next-transform"
          />

          <sid-range-slider
            label="Opacity"
            rule-name="opacity"
            :dimensions="[]"
            default-dimension=""
            :sid="`.--${section.sid}--slider > .slide-container.next > .slide`"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </template>

        <template #Active>
          <sid-transform
            :sid="`.--${section.sid}--slider > .slide-container.active > .slide`"
            rule-name="transform"
            :section="section"
            var="--active-transform"
          />

          <sid-range-slider
            label="Opacity"
            rule-name="opacity"
            :dimensions="[]"
            default-dimension=""
            :sid="`.--${section.sid}--slider > .slide-container.active > .slide`"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </template>

        <template #Behind>
          <sid-transform
            :sid="`.--${section.sid}--slider > .slide-container.behind > .slide`"
            rule-name="transform"
            :section="section"
            var="--behind-transform"
          />

          <sid-range-slider
            label="Opacity"
            rule-name="opacity"
            :dimensions="[]"
            default-dimension=""
            :sid="`.--${section.sid}--slider > .slide-container.behind > .slide`"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </template>

        <template #Forward>
          <sid-transform
            :sid="`.--${section.sid}--slider > .slide-container.forward > .slide`"
            rule-name="transform"
            :section="section"
            var="--forward-transform"
          />

          <sid-range-slider
            label="Opacity"
            rule-name="opacity"
            :dimensions="[]"
            default-dimension=""
            :sid="`.--${section.sid}--slider > .slide-container.forward > .slide`"
            :min="0"
            :max="1"
            :step="0.1"
          />
        </template>
      </rule-tabs>
    </SidbearSettingsGroup>

    <SidbearSettingsGroup title="Prev Button">
      <rule-tabs>
        <template #Normal>
          <b-form-group>
            <icon-select
              v-model="section.options.content.carousel.prevIcon"
            />
          </b-form-group>

          <sid-upload-image
            :sid="`.--${section.sid}--prev-btn`"
            rule-name="background-image"
          />

          <sid-background
            :sid="`.--${section.sid}--prev-btn`"
          />

          <sid-range-slider
            :sid="`.--${section.sid}--prev-btn i`"
            label="Font Size"
            rule-name="font-size"
          />

          <b-form-group label="Text Color">
            <sid-color
              :sid="`.--${section.sid}--prev-btn i`"
              rule-name="color"
            />
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="pb-0">Border Type</label>

              <sid-select
                :sid="`.--${section.sid}--prev-btn`"
                rule-name="border-style"
                :searchable="false"
                class="w-[118px]"
                :options="[{label: 'Solid', value: 'solid'}, {label: 'Dashed', value: 'dashed'}, {label: 'Dotted', value: 'dotted'}, {label: 'Double', value: 'double'}, {label: 'Outset', value: 'outset'}]"
              />
            </div>
          </b-form-group>

          <b-form-group>
            <sid-linked-group
              :sid="`.--${section.sid}--prev-btn`"
              label="Border Width"
              rule-name="border"
              class="!mb-[7px]"
              :angles="[
                {
                  text: '',
                  value: 'top-width'
                },
                {
                  text: '',
                  value: 'right-width'
                },
                {
                  text: '',
                  value: 'bottom-width'
                },
                {
                  text: '',
                  value: 'left-width'
                }
              ]"
            />

            <div class="flex w-full justify-end">
              <sid-color
                :sid="`.--${section.sid}--prev-btn`"
                rule-name="border-color"
              />
            </div>
          </b-form-group>

          <sid-linked-group
            label="Radius"
            :sid="`.--${section.sid}--prev-btn`"
            rule-name="border"

            :angles="[
              {
                text: '',
                value: 'top-left-radius'
              },
              {
                text: '',
                value: 'top-right-radius'
              },
              {
                text: '',
                value: 'bottom-left-radius'
              },
              {
                text: '',
                value: 'bottom-right-radius'
              }
            ]"
          />
        </template>

        <template #Hover>
          <sid-background
            :sid="`.--${section.sid}--prev-btn`"
            presudo=":hover"
          />

          <sid-range-slider
            :sid="`.--${section.sid}--prev-btn`"
            rule-name="font-size"
            label="Font Size"
            presudo=":hover"
          />

          <b-form-group label="Text Color">
            <sid-color
              :sid="`.--${section.sid}--prev-btn i`"
              rule-name="color"
              presudo=":hover"
            />
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="pb-0">Border Type</label>

              <sid-select
                :sid="`.--${section.sid}--prev-btn`"
                rule-name="border-style"
                :searchable="false"
                class="w-[118px]"
                presudo=":hover"
                :options="[{label: 'Solid', value: 'solid'}, {label: 'Dashed', value: 'dashed'}, {label: 'Dotted', value: 'dotted'}, {label: 'Double', value: 'double'}, {label: 'Outset', value: 'outset'}]"
              />
            </div>
          </b-form-group>

          <b-form-group>
            <sid-linked-group
              :sid="`.--${section.sid}--prev-btn`"
              label="Border Width"
              rule-name="border"
              class="!mb-[7px]"
              presudo=":hover"
              :angles="[
                {
                  text: '',
                  value: 'top-width'
                },
                {
                  text: '',
                  value: 'right-width'
                },
                {
                  text: '',
                  value: 'bottom-width'
                },
                {
                  text: '',
                  value: 'left-width'
                }
              ]"
            />

            <div class="flex w-full justify-end">
              <sid-color
                :sid="`.--${section.sid}--prev-btn`"
                presudo=":hover"
                rule-name="border-color"
              />
            </div>
          </b-form-group>

          <sid-linked-group
            label="Radius"
            :sid="`.--${section.sid}--prev-btn`"
            rule-name="border"
            presudo=":hover"

            :angles="[
              {
                text: '',
                value: 'top-left-radius'
              },
              {
                text: '',
                value: 'top-right-radius'
              },
              {
                text: '',
                value: 'bottom-left-radius'
              },
              {
                text: '',
                value: 'bottom-right-radius'
              }
            ]"
          />
        </template>
      </rule-tabs>

      <sid-width-and-height
        :sid="`.--${section.sid}--prev-btn`"
      />

      <sid-range-slider
        label="Position X"
        :sid="`.--${section.sid}--prev-btn`"
        rule-name="left"
        :dimensions="[]"
        default-dimension="%"
        var="--left"
      />

      <sid-range-slider
        label="Position Y"
        :sid="`.--${section.sid}--prev-btn`"
        rule-name="top"
        :dimensions="[]"
        default-dimension="%"
        var="--top"
      />

      <sid-translate
        :sid="`.--${section.sid}--prev-btn`"
        rule-name="translate"
        dimension="%"
        :min="-100"
        :max="100"
        :step="1"
      />

      <sid-linked-group
        label="Padding"
        :sid="`.--${section.sid}--prev-btn`"
        rule-name="padding"
        :min="0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />

      <sid-linked-group
        label="Margin"
        :sid="`.--${section.sid}--prev-btn`"
        rule-name="margin"
        :min="0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />
    </SidbearSettingsGroup>

    <SidbearSettingsGroup title="Next Button">
      <rule-tabs>
        <template #Normal>
          <b-form-group>
            <icon-select
              v-model="section.options.content.carousel.nextIcon"
            />
          </b-form-group>

          <sid-upload-image
            :sid="`.--${section.sid}--next-btn`"
            rule-name="background-image"
          />

          <sid-background
            :sid="`.--${section.sid}--next-btn`"
          />

          <sid-range-slider
            :sid="`.--${section.sid}--next-btn i`"
            label="Font Size"
            rule-name="font-size"
          />

          <b-form-group label="Text Color">
            <sid-color
              :sid="`.--${section.sid}--next-btn i`"
              rule-name="color"
            />
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="pb-0">Border Type</label>

              <sid-select
                :sid="`.--${section.sid}--next-btn`"
                rule-name="border-style"
                :searchable="false"
                class="w-[118px]"
                :options="[{label: 'Solid', value: 'solid'}, {label: 'Dashed', value: 'dashed'}, {label: 'Dotted', value: 'dotted'}, {label: 'Double', value: 'double'}, {label: 'Outset', value: 'outset'}]"
              />
            </div>
          </b-form-group>

          <b-form-group>
            <sid-linked-group
              :sid="`.--${section.sid}--next-btn`"
              label="Border Width"
              rule-name="border"
              class="!mb-[7px]"
              :angles="[
                {
                  text: '',
                  value: 'top-width'
                },
                {
                  text: '',
                  value: 'right-width'
                },
                {
                  text: '',
                  value: 'bottom-width'
                },
                {
                  text: '',
                  value: 'left-width'
                }
              ]"
            />

            <div class="flex w-full justify-end">
              <sid-color
                :sid="`.--${section.sid}--next-btn`"
                rule-name="border-color"
              />
            </div>
          </b-form-group>

          <sid-linked-group
            label="Radius"
            :sid="`.--${section.sid}--next-btn`"
            rule-name="border"

            :angles="[
              {
                text: '',
                value: 'top-left-radius'
              },
              {
                text: '',
                value: 'top-right-radius'
              },
              {
                text: '',
                value: 'bottom-left-radius'
              },
              {
                text: '',
                value: 'bottom-right-radius'
              }
            ]"
          />
        </template>

        <template #Hover>
          <sid-background
            :sid="`.--${section.sid}--next-btn`"
            presudo=":hover"
          />

          <sid-range-slider
            :sid="`.--${section.sid}--next-btn`"
            rule-name="font-size"
            label="Font Size"
            presudo=":hover"
          />

          <b-form-group label="Text Color">
            <sid-color
              :sid="`.--${section.sid}--next-btn i`"
              rule-name="color"
              presudo=":hover"
            />
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="pb-0">Border Type</label>

              <sid-select
                :sid="`.--${section.sid}--next-btn`"
                rule-name="border-style"
                :searchable="false"
                class="w-[118px]"
                presudo=":hover"
                :options="[{label: 'Solid', value: 'solid'}, {label: 'Dashed', value: 'dashed'}, {label: 'Dotted', value: 'dotted'}, {label: 'Double', value: 'double'}, {label: 'Outset', value: 'outset'}]"
              />
            </div>
          </b-form-group>

          <b-form-group>
            <sid-linked-group
              :sid="`.--${section.sid}--next-btn`"
              label="Border Width"
              rule-name="border"
              class="!mb-[7px]"
              presudo=":hover"
              :angles="[
                {
                  text: '',
                  value: 'top-width'
                },
                {
                  text: '',
                  value: 'right-width'
                },
                {
                  text: '',
                  value: 'bottom-width'
                },
                {
                  text: '',
                  value: 'left-width'
                }
              ]"
            />

            <div class="flex w-full justify-end">
              <sid-color
                :sid="`.--${section.sid}--next-btn`"
                presudo=":hover"
                rule-name="border-color"
              />
            </div>
          </b-form-group>

          <sid-linked-group
            label="Radius"
            :sid="`.--${section.sid}--next-btn`"
            rule-name="border"
            presudo=":hover"

            :angles="[
              {
                text: '',
                value: 'top-left-radius'
              },
              {
                text: '',
                value: 'top-right-radius'
              },
              {
                text: '',
                value: 'bottom-left-radius'
              },
              {
                text: '',
                value: 'bottom-right-radius'
              }
            ]"
          />
        </template>
      </rule-tabs>

      <sid-width-and-height
        :sid="`.--${section.sid}--next-btn`"
      />

      <sid-range-slider
        label="Position X"
        :sid="`.--${section.sid}--next-btn`"
        rule-name="left"
        :dimensions="[]"
        default-dimension="%"
        var="--left"
      />

      <sid-range-slider
        label="Position Y"
        :sid="`.--${section.sid}--next-btn`"
        rule-name="top"
        :dimensions="[]"
        default-dimension="%"
        var="--top"
      />

      <sid-translate
        :sid="`.--${section.sid}--next-btn`"
        rule-name="translate"
        dimension="%"
        :min="-100"
        :max="100"
        :step="1"
      />

      <sid-linked-group
        label="Padding"
        :sid="`.--${section.sid}--next-btn`"
        rule-name="padding"
        :min="0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />

      <sid-linked-group
        label="Margin"
        :sid="`.--${section.sid}--next-btn`"
        rule-name="margin"
        :min="0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      title="Padding & Margins"
    >
      <sid-linked-group
        label="Padding"
        :sid="`.--${section.sid}--slider`"
        rule-name="padding"
        :min="0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />

      <sid-linked-group
        label="Margin"
        :sid="`.--${section.sid}--slider`"
        rule-name="margin"
        :min="-2000"
        :max="2000"
        class="mb-0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />
    </SidbearSettingsGroup>
  </div>
</template>

<script>
import ModMixin from '@/components/mixins/ModMixin'
import SidRangeSlider from '@/components/editor/components/sid-controls/SidRangeSlider'
import SidbearSettingsGroup from '@/components/editor/components/SidbearSettingsGroup'
import RuleTabs from '@/components/editor/components/RuleTabs'
import SidTransform from '@/components/editor/components/sid-controls/sid-control-pressets/transform/SidTransform'
import LinkedControlItem from '@/components/editor/components/linked-control-group/LinkedControlItem'
import SidTranslate from '@/components/editor/components/sid-controls/sid-control-pressets/transform/SidTranslate'
import SidLinkedGroup from '@/components/editor/components/sid-controls/SidLinkedGroup'
import SidBackground
  from '@/components/editor/components/sid-controls/sid-control-pressets/background-color/SidBackground'
import SidWidthAndHeight from '@/components/editor/components/sid-controls/sid-control-pressets/SidWidthAndHeight'
import SidSelect from '@/components/editor/components/sid-controls/SidSelect'
import SidColor from '@/components/editor/components/sid-controls/SidColor'
import IconSelect from '@/components/editor/components/IconSelect'
import SidUploadImage
  from '@/components/editor/components/sid-controls/sid-control-pressets/background-image/SidUploadImage'
import PickElement from '@/components/editor/controls/PickElement'
import draggable from 'vuedraggable'
import CarouselDragItem from '@/components/editor/components/CarouselDragItem'
import EventsMixin from '@/components/editor/mixins/EventsMixin'
import SidBoxShadow from '@/components/editor/components/sid-controls/sid-control-pressets/box-shadow/SidBoxShadow'

export default {
  name: 'CarouselModV2',
  components: {
    SidBoxShadow,
    CarouselDragItem,
    draggable,
    PickElement,
    SidUploadImage,
    IconSelect,
    SidColor,
    SidSelect,
    SidWidthAndHeight,
    SidBackground,
    SidLinkedGroup,
    SidTranslate, LinkedControlItem, SidTransform, RuleTabs, SidbearSettingsGroup, SidRangeSlider},
  mixins: [ModMixin, EventsMixin],
  data () {
    return {
      selectedSlide: null
    }
  },
  computed: {
    activeSlide: {
      get () {
        return this.selectedSlide
      },
      set (val) {
        this.selectedSlide = val
      }
    }
  },
  methods: {
    selectSlide (uid, index) {
      this.activeSlide = uid
      this.fire('onServiceSlideChange', {slide: index})
    },
    deleteSlide (index, slide) {
      this.clearStyles(document.querySelector(`[data-sid="${this.section.sid}--${slide.uid}"]`))

      this.section.options.content.carousel.slides.splice(index, 1)
      this.activeSlide = null
    },
    addSlide () {
      this.section.options.content.carousel.slides.push({
        uid: this.section.options.content.carousel.slides.length + 1,
        options: {},
        content: []
      })
    }
  }
}
</script>
